"use client";

import * as React from "react";
import { clsx } from "clsx";

import { formatValue } from "@/lib/reports/labels";
import styles from "./KpiCard.module.css";

export type KpiColor = "blue" | "pink" | "orange" | "neutral";

export type KpiFormat =
  | "number"
  | "compact"
  | "percent"
  | "currency"
  | "duration_minutes"
  | "duration_seconds";

export interface KpiCardProps {
  value: number | null | undefined;
  format?: KpiFormat;
  unit?: string;
  caption?: string;
  /** Comparison-period value used to compute delta. */
  compareValue?: number | null;
  /** Human label for what the compare is against — "vs previous period",
   *  "vs same time last year", etc. Shown next to the delta. */
  compareLabel?: string;
  /** Sparkline data points (latest last). */
  series?: number[];
  color?: KpiColor;
  /** Show a goal indicator (filled bar inside spark) when provided. */
  goalValue?: number;
}

export function KpiCard({
  value,
  format = "number",
  unit,
  caption,
  compareValue,
  compareLabel,
  series,
  color = "blue",
  goalValue,
}: KpiCardProps) {
  const display = formatKpiNumber(value, format);
  const delta = computeKpiDelta(value, compareValue);
  const direction = kpiDirection(delta);
  const goalProgress = computeGoalProgress(value, goalValue);
  const goalBucket = bucketGoalProgress(goalProgress);

  return (
    <div
      className={clsx(styles.root, {
        [styles.colorBlue]: color === "blue",
        [styles.colorPink]: color === "pink",
        [styles.colorOrange]: color === "orange",
        [styles.colorNeutral]: color === "neutral",
      })}
    >
      <div className={styles.valueRow}>
        {display !== null ? (
          <span className={styles.value} data-kpi-value>
            {display}
          </span>
        ) : (
          // Distinct "no data" treatment — a thin em-dash at .value font
          // size is invisible at thumbnail/snapshot widths. Use a smaller
          // muted "No data yet" string that reads as a deliberate empty
          // state instead of a broken-looking card. data-kpi-value still
          // set so the snapshot pipeline knows the block has rendered.
          <span className={styles.empty} data-kpi-value data-kpi-empty>
            No data yet
          </span>
        )}
        {unit && display !== null ? <span className={styles.unit}>{unit}</span> : null}
        {delta !== null ? (
          <span
            className={clsx(
              styles.delta,
              direction === "up" && styles.deltaUp,
              direction === "down" && styles.deltaDown,
              direction === "flat" && styles.deltaFlat,
            )}
            title={compareLabel ? `vs ${compareLabel}` : undefined}
          >
            {direction === "up" ? "▲" : direction === "down" ? "▼" : "■"} {(delta * 100).toFixed(1)}%
            {compareLabel ? <span className={styles.compareLabel}> vs {compareLabel}</span> : null}
          </span>
        ) : compareLabel ? (
          <span className={clsx(styles.delta, styles.deltaFlat)} title={`No usable ${compareLabel} value was returned`}>
            No comparison data
          </span>
        ) : null}
        {goalBucket !== null && goalProgress !== null ? (
          <span
            className={clsx(
              styles.goalPill,
              goalBucket === "met" && styles.goalPillMet,
              goalBucket === "near" && styles.goalPillNear,
              goalBucket === "far" && styles.goalPillFar,
            )}
            title={`Goal: ${formatKpiNumber(goalValue ?? null, format) ?? "—"}`}
          >
            {Math.round(goalProgress * 100)}% of goal
          </span>
        ) : null}
      </div>
      {series && series.length > 1 ? (
        <Sparkline series={series} color={color} goalValue={goalValue} />
      ) : null}
      {caption ? (
        <div className={styles.caption} title={caption}>
          {caption}
        </div>
      ) : null}
    </div>
  );
}

export type SparklineGeometry = {
  width: number;
  height: number;
  points: string;
  stroke: string;
  isFlat: boolean;
  goalY: number | undefined;
  lastX: number;
  lastY: number;
};

export function computeSparklineGeometry(
  series: number[],
  color: KpiColor,
  goalValue?: number,
): SparklineGeometry {
  const width = 240;
  const height = 36;
  const padX = 1;
  const padY = 4;
  const max = Math.max(...series, goalValue ?? -Infinity);
  const min = Math.min(...series, goalValue ?? Infinity);
  const range = max - min;
  const isFlat = !Number.isFinite(range) || range === 0;
  const stroke =
    color === "pink"
      ? "#e50b71"
      : color === "orange"
        ? "#d97706"
        : color === "neutral"
          ? "#374151"
          : "#13319a";
  const safeRange = isFlat ? 1 : range;
  const points = series
    .map((v, i) => {
      const x = padX + (i / Math.max(1, series.length - 1)) * (width - padX * 2);
      const y = isFlat
        ? height / 2
        : padY + (1 - (v - min) / safeRange) * (height - padY * 2);
      return `${x},${y}`;
    })
    .join(" ");
  const goalY =
    goalValue !== undefined && !isFlat
      ? padY + (1 - (goalValue - min) / safeRange) * (height - padY * 2)
      : undefined;
  const lastX = padX + (series.length - 1) / Math.max(1, series.length - 1) * (width - padX * 2);
  const lastY = isFlat
    ? height / 2
    : padY + (1 - (series[series.length - 1] - min) / safeRange) * (height - padY * 2);
  return { width, height, points, stroke, isFlat, goalY, lastX, lastY };
}

function Sparkline({
  series,
  color,
  goalValue,
}: {
  series: number[];
  color: KpiColor;
  goalValue?: number;
}) {
  const { width, height, points, stroke, isFlat, goalY, lastX, lastY } =
    computeSparklineGeometry(series, color, goalValue);
  const padX = 1;

  return (
    <div className={styles.sparklineWrap}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" width="100%" height={height} aria-hidden>
        {goalY !== undefined ? (
          <line x1={padX} x2={width - padX} y1={goalY} y2={goalY} stroke={stroke} strokeOpacity={0.25} strokeDasharray="4 4" />
        ) : null}
        <polyline
          points={points}
          fill="none"
          stroke={stroke}
          strokeOpacity={isFlat ? 0.35 : 1}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {!isFlat ? (
          <circle cx={lastX} cy={lastY} r={2.5} fill={stroke} />
        ) : null}
      </svg>
    </div>
  );
}

/**
 * Compute the relative delta between value and compareValue.
 * Pure; exported for tests. Returns null when either side is missing
 * or compareValue is zero (would divide by zero).
 */
export function computeKpiDelta(
  value: number | null | undefined,
  compareValue: number | null | undefined,
): number | null {
  if (typeof value !== "number" || typeof compareValue !== "number") return null;
  if (compareValue === 0) return null;
  return (value - compareValue) / compareValue;
}

export type KpiDirection = "up" | "down" | "flat";

/**
 * Convert a delta into a tri-state direction. Uses a 0.5 % deadband
 * so noise from rounding doesn't flicker between up/down on small
 * day-over-day jitter.
 */
export function kpiDirection(delta: number | null): KpiDirection {
  if (delta === null) return "flat";
  if (delta > 0.005) return "up";
  if (delta < -0.005) return "down";
  return "flat";
}

export function computeGoalProgress(
  value: number | null | undefined,
  goalValue: number | undefined,
): number | null {
  if (typeof value !== "number" || typeof goalValue !== "number" || goalValue === 0) {
    return null;
  }
  return value / goalValue;
}

export type GoalBucket = "met" | "near" | "far";

export function bucketGoalProgress(progress: number | null): GoalBucket | null {
  if (progress === null) return null;
  if (progress >= 1) return "met";
  if (progress >= 0.7) return "near";
  return "far";
}

// Delegate to the shared formatter so the KPI card, chart axes, and table
// cells stay consistent (single source of truth in labels.ts:formatValue).
// The only KPI-specific concern is the empty state: formatValue renders "—"
// for missing/non-finite values, but the card wants null so it can swap in a
// distinct "No data yet" treatment — so we map that sentinel back to null.
export function formatKpiNumber(value: number | null | undefined, format: KpiFormat): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const formatted = formatValue(value, format);
  return formatted === "—" ? null : formatted;
}
